import _ from "lodash";
import React from "react";
import PropTypes from "prop-types";
import { createUseStyles } from "react-jss";
import { InlineIcon } from "@iconify/react";
import { Formik, Form, Field } from "formik";
import { StyleTypeContext, TranslationContext } from "../../../umbilical_tools";
import { useContext } from "react";
import { cStyler } from "../../../helpers";
import jssVariables from "../../../jss_setup";
import { modalGenericStyles } from "../../../jss_setup/modals";
import { useDispatch, useSelector } from "react-redux";
import { useCallback } from "react";
import { trainSearchFormUpdate } from "../../../actions/search/trains_search";
import { trainPax } from "../modals";
import { toast } from "react-toastify";

export const trainPaxOnlyMSF = {
  ...trainPax,
  children: {
    from: trainPax.youths.from,
    to: 16,
  },
  youths: {
    from: 17,
    to: trainPax.youths.to,
  },
};

const trainPaxRanges = {
  adults: trainPaxOnlyMSF.adults.to - trainPaxOnlyMSF.adults.from + 1,
  seniors: trainPaxOnlyMSF.seniors.to - trainPaxOnlyMSF.seniors.from + 1,
  youths: trainPaxOnlyMSF.youths.to - trainPaxOnlyMSF.youths.from + 1,
  children: trainPaxOnlyMSF.children.to - trainPaxOnlyMSF.children.from + 1,
};

const railPaxPickerStyles = createUseStyles({
  ...modalGenericStyles,
  RailPaxPickerModal: {
    ...modalGenericStyles.modal,
  },
  card: {
    ...modalGenericStyles.card,
    top: `calc(${jssVariables.navbar_height} * 1.5)`,
    transform: "translateX(-50%)",
    width: "90vw",
    maxHeight: "max-content",
    padding: `calc(${jssVariables.normal_gap} * 2)`,
  },
  form: (props) => ({
    overflowY: "auto",
    maxHeight: "70vh",
    ..._.get(props, "customStyles.form", {}),
  }),
  body: modalGenericStyles.cardBody,
  actions: modalGenericStyles.cardActions,
  paxGroup: (props) => ({
    display: "grid",
    gridTemplateColumns: "max-content 1fr max-content",
    placeItems: "center",
    placeContent: "center",
    fontSize: "large",
    padding: [`calc(${jssVariables.normal_gap} / 2)`, jssVariables.normal_gap],
    border: `2px solid ${jssVariables.colors.base_color}`,
    color: jssVariables.colors.base_color,
    ..._.get(props, "customStyles.paxGroup", {}),
  }),
  paxBtn: (props) => ({
    borderRadius: "50%",
    height: "2.5rem",
    width: "2.5rem",
    fontSize: "larger",
    display: "inline-grid",
    placeItems: "center",
    padding: 0,
    background: "none",
    border: `2px solid ${jssVariables.colors.base_color}`,
    ..._.get(props, "customStyles.paxBtn", {}),
  }),
  paxPlusBtn: (props) => ({
    background: jssVariables.colors.base_color,
    color: "white",
    ..._.get(props, "customStyles.paxPlusBtn", {}),
  }),
  ages: {
    display: "grid",
    gridGap: jssVariables.normal_gap,
    fontSize: "medium",
  },
  ageItem: (props) => ({
    display: "grid",
    gridGap: `calc(${jssVariables.normal_gap} / 2)`,
    alignItems: "center",
    textAlign: "left",
    gridAutoRows: "max-content",
    "& select": {
      padding: [jssVariables.half_gap, jssVariables.normal_gap],
      border: `2px solid ${jssVariables.colors.base_color}`,
    },
    ..._.get(props, "customStyles.ageItem", {}),
  }),
  applyBtn: (props) => ({
    padding: jssVariables.normal_gap,
    background: jssVariables.colors.base_color,
    color: "white",
    fontWeight: "bold",
    fontSize: "large",
    ..._.get(props, "customStyles.applyBtn", {}),
  }),
  cancelBtn: (props) => ({
    padding: jssVariables.normal_gap,
    background: "none",
    color: jssVariables.colors.base_color,
    border: `2px solid ${jssVariables.colors.base_color}`,
    fontWeight: "bold",
    fontSize: "large",
    ..._.get(props, "customStyles.cancelBtn", {}),
  }),
  divider: (props) => ({
    width: "100%",
    height: "2px",
    background: jssVariables.colors.base_color,
    margin: [jssVariables.normal_gap, 0],
    ..._.get(props, "customStyles.divider", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    card: { width: "30vw" },
  },
});
const RailPaxPickerModal = ({ onCancel }) => {
  const { t } = useContext(TranslationContext);
  const { type, brand } = useContext(StyleTypeContext);
  const dispatch = useDispatch();

  const customStyles = cStyler({
    type,
    brand,
    objName: "RailPaxPickerModal",
  });
  const classes = railPaxPickerStyles({ customStyles });

  const search_form = useSelector((state) => state.TrainSearchForm);

  const onFormUpdate = useCallback(
    ({ form }) => dispatch(trainSearchFormUpdate({ form })),
    [dispatch]
  );

  function createInitialValues() {
    return {
      adults: search_form.adults,
      adults_ages: search_form.adults_ages,
      seniors: search_form.seniors,
      seniors_ages: search_form.seniors_ages,
      youths_ages: search_form.youths_ages.filter(
        (age) => age >= trainPaxOnlyMSF.youths.from
      ),
      children_ages: search_form.youths_ages.filter(
        (age) => age < trainPaxOnlyMSF.youths.from
      ),
      youths: search_form.youths_ages.filter(
        (age) => age >= trainPaxOnlyMSF.youths.from
      ).length,
      children: search_form.youths_ages.filter(
        (age) => age < trainPaxOnlyMSF.youths.from
      ).length,
    };
  }

  return (
    <div className={classes.RailPaxPickerModal}>
      <div className={classes.card}>
        <Formik
          initialValues={createInitialValues()}
          enableReinitialize={true}
          onSubmit={(values) => {
            const form = _.cloneDeep(values);
            if (
              form.adults + form.seniors + form.youths === 0 &&
              form.children > 0
            ) {
              toast.error(t("atLeastOneAdult"));
              return;
            }
            form.adults_ages = form.adults_ages.map((age) => parseInt(age));
            form.seniors_ages = form.seniors_ages.map((age) => parseInt(age));
            form.youths_ages = [
              ...form.youths_ages.map((age) => parseInt(age)),
              ...form.children_ages.map((age) => parseInt(age)),
            ];
            form.youths = form.youths + form.children;
            delete form.children_ages;
            delete form.children;

            onFormUpdate({ form });
            onCancel();
          }}>
          {({ values, setFieldValue }) => (
            <Form className={classes.form}>
              <div className={classes.body}>
                <div className={classes.paxGroup}>
                  <button
                    type="button"
                    className={classes.paxBtn}
                    onClick={() => {
                      if (values.adults > 0) {
                        setFieldValue("adults", values.adults - 1);
                        setFieldValue(
                          "adults_ages",
                          values.adults_ages.slice(0, values.adults - 1)
                        );
                      }
                    }}>
                    <InlineIcon icon="fa6-solid:minus" />
                  </button>
                  <strong>
                    {`${t("adults")} (${trainPaxOnlyMSF.adults.from} - ${
                      trainPaxOnlyMSF.adults.to
                    }yo): ${values.adults}`}
                  </strong>
                  <button
                    type="button"
                    className={`${classes.paxBtn} ${classes.paxPlusBtn}`}
                    onClick={() => {
                      if (values.adults < 9) {
                        setFieldValue("adults", values.adults + 1);
                        setFieldValue("adults_ages", [
                          ...values.adults_ages,
                          trainPaxOnlyMSF.adults.from,
                        ]);
                      }
                    }}>
                    <InlineIcon icon="fa6-solid:plus" />
                  </button>
                </div>
                <div className={classes.ages}>
                  {new Array(values.adults).fill(1).map((x, idx) => (
                    <div key={idx} className={classes.ageItem}>
                      <strong>
                        {` ${t("adult")} ${idx + 1} ${t("age")}:`}
                      </strong>
                      <Field name={`adults_ages.${idx}`} as="select" key={idx}>
                        {new Array(trainPaxRanges.adults)
                          .fill(1)
                          .map((age, aidx) => (
                            <option
                              value={aidx + trainPaxOnlyMSF.adults.from}
                              key={aidx}>
                              {`${aidx + trainPaxOnlyMSF.adults.from} ${t(
                                "yearsOld"
                              )}.`}
                            </option>
                          ))}
                      </Field>
                    </div>
                  ))}
                </div>
                {values.seniors > 0 && <div className={classes.divider} />}
                {/* Seniors */}
                <div className={classes.paxGroup}>
                  <button
                    type="button"
                    className={classes.paxBtn}
                    onClick={() => {
                      if (values.seniors > 0) {
                        setFieldValue("seniors", values.seniors - 1);
                        setFieldValue(
                          "seniors_ages",
                          values.seniors_ages.slice(0, values.seniors - 1)
                        );
                      }
                    }}>
                    <InlineIcon icon="fa6-solid:minus" />
                  </button>
                  <strong>
                    {`${t("seniors")} (${trainPaxOnlyMSF.seniors.from}+ yo): ${
                      values.seniors
                    }`}
                  </strong>
                  <button
                    type="button"
                    className={`${classes.paxBtn} ${classes.paxPlusBtn}`}
                    onClick={() => {
                      if (values.seniors < 9) {
                        setFieldValue("seniors", values.seniors + 1);
                        setFieldValue("seniors_ages", [
                          ...values.seniors_ages,
                          trainPaxOnlyMSF.seniors.from,
                        ]);
                      }
                    }}>
                    <InlineIcon icon="fa6-solid:plus" />
                  </button>
                </div>
                <div className={classes.ages}>
                  {new Array(values.seniors).fill(1).map((x, idx) => (
                    <div key={idx} className={classes.ageItem}>
                      <strong>
                        {`${t("senior")} ${idx + 1} ${t("age")}:`}
                      </strong>
                      <Field name={`seniors_ages.${idx}`} as="select" key={idx}>
                        {new Array(trainPaxRanges.seniors)
                          .fill(1)
                          .map((age, aidx) => (
                            <option
                              value={aidx + trainPaxOnlyMSF.seniors.from}
                              key={aidx}>
                              {`${
                                aidx + trainPaxOnlyMSF.seniors.from <= 100
                                  ? aidx + trainPaxOnlyMSF.seniors.from
                                  : ""
                              }
                              ${
                                aidx + trainPaxOnlyMSF.seniors.from > 100
                                  ? "100+"
                                  : ""
                              }
                              ${t("yearsOld")}.`}
                            </option>
                          ))}
                      </Field>
                    </div>
                  ))}
                </div>
                {values.seniors > 0 && <div className={classes.divider} />}
                {/* Youths */}
                <div className={classes.paxGroup}>
                  <button
                    type="button"
                    className={classes.paxBtn}
                    onClick={() => {
                      if (values.youths > 0) {
                        setFieldValue("youths", values.youths - 1);
                        setFieldValue(
                          "youths_ages",
                          values.youths_ages.slice(0, values.youths - 1)
                        );
                      }
                    }}>
                    <InlineIcon icon="fa6-solid:minus" />
                  </button>
                  <strong>
                    {`${t("youths")} (${trainPaxOnlyMSF.youths.from} - ${
                      trainPaxOnlyMSF.youths.to
                    }yo): ${values.youths}`}
                  </strong>
                  <button
                    type="button"
                    className={`${classes.paxBtn} ${classes.paxPlusBtn}`}
                    onClick={() => {
                      if (values.youths < 9) {
                        setFieldValue("youths", values.youths + 1);
                        setFieldValue("youths_ages", [
                          ...values.youths_ages,
                          trainPaxOnlyMSF.youths.from,
                        ]);
                      }
                    }}>
                    <InlineIcon icon="fa6-solid:plus" />
                  </button>
                </div>
                <div className={classes.ages}>
                  {new Array(values.youths).fill(1).map((x, idx) => (
                    <div key={idx} className={classes.ageItem}>
                      <strong>{`${t("youth")} ${idx + 1} ${t("age")}:`}</strong>
                      <Field name={`youths_ages.${idx}`} as="select" key={idx}>
                        {new Array(trainPaxRanges.youths)
                          .fill(1)
                          .map((age, index) => (
                            <option
                              value={index + trainPaxOnlyMSF.youths.from}
                              key={index}>
                              {`${index + trainPaxOnlyMSF.youths.from}
                              ${
                                index + trainPaxOnlyMSF.youths.from === 1
                                  ? t("yearOld")
                                  : t("yearsOld")
                              }.`}
                            </option>
                          ))}
                      </Field>
                    </div>
                  ))}
                </div>
                {values.seniors > 0 && <div className={classes.divider} />}
                {/* Children */}
                <div className={classes.paxGroup}>
                  <button
                    type="button"
                    className={classes.paxBtn}
                    onClick={() => {
                      if (values.children > 0) {
                        setFieldValue("children", values.children - 1);
                        setFieldValue(
                          "children_ages",
                          values.children_ages.slice(0, values.children - 1)
                        );
                      }
                    }}>
                    <InlineIcon icon="fa6-solid:minus" />
                  </button>
                  <strong>
                    {`${t("children")} (${trainPaxOnlyMSF.children.from} - ${
                      trainPaxOnlyMSF.children.to
                    }yo): ${values.children}`}
                  </strong>
                  <button
                    type="button"
                    className={`${classes.paxBtn} ${classes.paxPlusBtn}`}
                    onClick={() => {
                      if (values.children < 9) {
                        setFieldValue("children", values.children + 1);
                        setFieldValue("children_ages", [
                          ...values.children_ages,
                          trainPaxOnlyMSF.children.from,
                        ]);
                      }
                    }}>
                    <InlineIcon icon="fa6-solid:plus" />
                  </button>
                </div>
                <div className={classes.ages}>
                  {new Array(values.children).fill(1).map((x, idx) => (
                    <div key={idx} className={classes.ageItem}>
                      <strong>{`${t("youth")} ${idx + 1} ${t("age")}:`}</strong>
                      <Field
                        name={`children_ages.${idx}`}
                        as="select"
                        key={idx}>
                        {new Array(trainPaxRanges.children)
                          .fill(1)
                          .map((age, index) => (
                            <option
                              value={index + trainPaxOnlyMSF.children.from}
                              key={index}>
                              {`${index + trainPaxOnlyMSF.children.from}
                              ${
                                index + trainPaxOnlyMSF.children.from === 1
                                  ? t("yearOld")
                                  : t("yearsOld")
                              }.`}
                            </option>
                          ))}
                      </Field>
                    </div>
                  ))}
                </div>
                {values.seniors > 0 && <div className={classes.divider} />}
              </div>
              <div className={classes.actions}>
                <button
                  className={classes.cancelBtn}
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    onCancel();
                  }}>
                  {t("cancel")}
                </button>
                <button className={classes.applyBtn} type="submit">
                  {t("apply")}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};
RailPaxPickerModal.defaultProps = {};
RailPaxPickerModal.propTypes = {
  onCancel: PropTypes.func.isRequired,
};
export default RailPaxPickerModal;
